import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Layout } from "@/components/site/Layout";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";

import Home from "@/pages/Home";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import {
  CapabilitiesLanding, PracticePage, CapabilityDetail, IndustriesLanding, IndustryPage,
  UseCasesLanding, UseCaseDetail, CountryPage, CountryTopicPage,
} from "@/pages/Catalog";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Leads from "@/pages/admin/Leads";
import Conversations from "@/pages/admin/Conversations";
import InsightsAdmin from "@/pages/admin/InsightsAdmin";
import InsightEditor from "@/pages/admin/InsightEditor";
import LeadershipAdmin from "@/pages/admin/LeadershipAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import SeoAdmin from "@/pages/admin/SeoAdmin";
import { BlueprintPage } from "@/pages/Catalog";
import ArticlePage from "@/pages/Article";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    requestAnimationFrame(() => { html.style.scrollBehavior = prev; });
  }, [pathname]);
  return null;
}
const Public = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <ScrollToTop />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<Public><Home /></Public>} />

            <Route path="/capabilities" element={<Public><CapabilitiesLanding /></Public>} />
            <Route path="/capabilities/:practice" element={<Public><PracticePage /></Public>} />
            <Route path="/supply-chain/:slug" element={<Public><CapabilityDetail prefix="supply-chain" /></Public>} />
            <Route path="/marketing/:slug" element={<Public><CapabilityDetail prefix="marketing" /></Public>} />
            <Route path="/learning/:slug" element={<Public><CapabilityDetail prefix="learning" /></Public>} />
            <Route path="/education/:slug" element={<Public><CapabilityDetail prefix="education" /></Public>} />
            <Route path="/ai-transformation/:slug" element={<Public><CapabilityDetail prefix="ai-transformation" /></Public>} />

            <Route path="/industries" element={<Public><IndustriesLanding /></Public>} />
            <Route path="/industries/:industry" element={<Public><IndustryPage /></Public>} />

            <Route path="/use-cases" element={<Public><UseCasesLanding /></Public>} />
            <Route path="/use-cases/:slug" element={<Public><UseCaseDetail /></Public>} />

            <Route path="/insights" element={<Public><Insights /></Public>} />
            <Route path="/insights/:slug" element={<Public><InsightArticle /></Public>} />
            <Route path="/about" element={<Public><About /></Public>} />
            <Route path="/contact" element={<Public><Contact /></Public>} />

            {/* Retained, fully-authored article routes (route-specific prerender) */}
            <Route path="/document-automation-singapore" element={<Public><ArticlePage /></Public>} />
            <Route path="/business-process-automation-singapore" element={<Public><ArticlePage /></Public>} />
            <Route path="/solutions/logistics/document-automation-singapore" element={<Public><ArticlePage /></Public>} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<Leads />} />
              <Route path="conversations" element={<Conversations />} />
              <Route path="insights" element={<InsightsAdmin />} />
              <Route path="insights/:id" element={<InsightEditor />} />
              <Route path="leadership" element={<LeadershipAdmin />} />
              <Route path="seo" element={<SeoAdmin />} />
              <Route path="settings" element={<SettingsAdmin />} />
            </Route>

            {/* Country / regional SEO pages (lowest priority, resolve via API) */}
            <Route path="/:country/:topic" element={<Public><CountryTopicPage /></Public>} />
            <Route path="/:country" element={<Public><CountryPage /></Public>} />

            <Route path="*" element={<Public><BlueprintPage /></Public>} />
          </Routes>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
